import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { reportsAPI, categoriesAPI, usersAPI } from "../services/api";
import { useToast } from "../hooks/use-toast";
import { useAuth } from "../contexts/AuthContext";
import {
  Download,
  BarChart3,
  TrendingUp,
  Users,
  Clock,
  Timer,
  CheckCircle,
  XCircle,
  ChevronRight,
  ChevronLeft,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { formatDuration, formatWhatsAppNumber } from "@/utils/formatters";
import { buildReportParams, getReportPages, REPORT_PAGE_SIZE } from "@/utils/reportFilters";

const REPORT_FILTER_DEFAULTS = {
  client_search: "",
  start_date: "",
  end_date: "",
  start_time: "",
  end_time: "",
  status: "_all_",
  assigned_to: "_all_",
  category: "_all_",
  priority: "_all_",
  sort_by: "created_at",
  sort_direction: "desc",
};

const Reports = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [attendants, setAttendants] = useState([]);
  const [filters, setFilters] = useState(REPORT_FILTER_DEFAULTS);
  const [filtersReady, setFiltersReady] = useState(false);
  const { toast } = useToast();

  const tableScrollRef = useRef(null);
  const hydratedFiltersUserRef = useRef(null);
  const reportRequestRef = useRef(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const reportFiltersStorageKey = user?.id
    ? `up2-suporte:report-filters:${user.id}`
    : null;

  useEffect(() => {
    if (!user?.id) {
      setFiltersReady(false);
      return;
    }

    if (hydratedFiltersUserRef.current === user.id) {
      setFiltersReady(true);
      return;
    }

    let savedFilters = {};
    try {
      savedFilters = JSON.parse(localStorage.getItem(reportFiltersStorageKey) || "{}");
    } catch (error) {
      console.warn("Não foi possível recuperar os filtros do relatório", error);
    }

    setFilters({ ...REPORT_FILTER_DEFAULTS, ...savedFilters });
    hydratedFiltersUserRef.current = user.id;
    setFiltersReady(true);
  }, [reportFiltersStorageKey, user?.id]);

  useEffect(() => {
    if (
      !filtersReady ||
      !reportFiltersStorageKey ||
      hydratedFiltersUserRef.current !== user?.id
    ) {
      return;
    }

    try {
      localStorage.setItem(reportFiltersStorageKey, JSON.stringify(filters));
    } catch (error) {
      console.warn("Não foi possível salvar os filtros do relatório", error);
    }
  }, [filters, filtersReady, reportFiltersStorageKey, user?.id]);

  const checkForScrollPosition = () => {
    if (tableScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tableScrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(Math.ceil(scrollLeft) < scrollWidth - clientWidth - 1);
    }
  };

  useEffect(() => {
    checkForScrollPosition();
    window.addEventListener("resize", checkForScrollPosition);
    return () => window.removeEventListener("resize", checkForScrollPosition);
  }, [reportData]);

  const scrollToRight = () => {
    if (tableScrollRef.current) {
      tableScrollRef.current.scrollBy({ left: 300, behavior: "smooth" });
    }
  };

  const scrollToLeft = () => {
    if (tableScrollRef.current) {
      tableScrollRef.current.scrollBy({ left: -300, behavior: "smooth" });
    }
  };

  useEffect(() => {
    if (!filtersReady || hydratedFiltersUserRef.current !== user?.id) return;

    setReportData(null);
    setAppliedFilters(null);
    loadCategories();
    loadAttendants();
    loadReport(filters);
    return () => { reportRequestRef.current += 1; };
  }, [filtersReady, user?.id]);

  const loadReport = async (activeFilters, notify = false) => {
    const requestId = ++reportRequestRef.current;
    setLoading(true);
    try {
      const params = buildReportParams(activeFilters);
      const response = await reportsAPI.getTickets({ ...params, page: 1, page_size: REPORT_PAGE_SIZE });
      if (requestId !== reportRequestRef.current) return;
      setReportData(response.data);
      setAppliedFilters(params);
      if (notify) toast({ title: "Relatório gerado", description: "Relatório processado com sucesso." });
    } catch (error) {
      if (requestId !== reportRequestRef.current) return;
      toast({
        title: "Erro ao gerar relatório",
        description: error.response?.data?.detail && typeof error.response.data.detail === "string"
          ? error.response.data.detail : error.response ? "Não foi possível gerar o relatório." : error.message,
        variant: "destructive",
      });
    } finally {
      if (requestId === reportRequestRef.current) setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await categoriesAPI.getAll();
      setCategories(response.data);
    } catch (error) {
      console.error("Error loading categories:", error);
    }
  };

  const loadAttendants = async () => {
    try {
      const response = await usersAPI.getAll();
      setAttendants(response.data);
    } catch (error) {
      console.error("Error loading attendants:", error);
    }
  };

  const changePage = async (page) => {
    if (loading || exporting || page === reportData.page || !appliedFilters) return;
    const requestId = ++reportRequestRef.current;
    setLoading(true);
    try {
      const response = await reportsAPI.getTickets({
        ...appliedFilters, page, page_size: REPORT_PAGE_SIZE, include_stats: false,
      });
      if (requestId !== reportRequestRef.current) return;
      setReportData((previous) => ({ ...previous, ...response.data }));
      tableScrollRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (error) {
      if (requestId !== reportRequestRef.current) return;
      toast({
        title: "Erro ao carregar página",
        description: "Tente novamente para continuar navegando pelo relatório.",
        variant: "destructive",
      });
    } finally {
      if (requestId === reportRequestRef.current) setLoading(false);
    }
  };

  const exportReport = async () => {
    if (!reportData || !appliedFilters || exporting || loading) return;
    setExporting(true);
    try {

      const headers = [
        "Protocolo",
        "Cliente",
        "Empresa",
        "Documento",
        "Telefone",
        "Nota",
        "Status",
        "Prioridade",
        "Categoria",
        "Atendente",
        "Data",
        "Duração (Minutos)",
      ].join(";");

      const formatRow = (t) => {
        const empresa =
          t.external_client_company ||
          t.external_client_data?.nome_fantasia ||
          "N/A";
        const documento =
          t.external_client_data?.cnpj || t.external_client_data?.cpf || "N/A";
        const telefoneExport =
          t.external_client_data?.real_phone || t.client_phone;

        const notaReal =
          t.rating !== null && t.rating !== undefined && t.rating >= 0
            ? t.rating
            : "Sem Nota";
        const duracaoMin = t.duration_seconds
          ? Math.round(t.duration_seconds / 60)
          : t.duration_minutes || 0;

        return [
          t.protocol,
          t.client_name?.replace(/;/g, " ") || "Sem nome",
          empresa?.replace(/;/g, " "),
          `"${documento}"`,
          `"${telefoneExport}"`,
          notaReal,
          statusLabels[t.status] || t.status,
          priorityLabels[t.priority] || t.priority,
          t.category || "N/A",
          t.closure_type === "potential" ? "Sem atendimento" : t.assigned_to_name || "N/A",
          new Date(t.created_at).toLocaleDateString("pt-BR"),
          t.closure_type === "potential" ? "" : duracaoMin,
        ].join(";");
      };

      const chunks = ["\uFEFF", headers];
      let page = 1;
      let totalPages = 1;
      do {
        const response = await reportsAPI.getTickets({
          ...appliedFilters, page, page_size: 1000, include_stats: false,
        });
        if (response.data.page !== page) throw new Error("Os resultados mudaram. Gere o relatório novamente.");
        chunks.push(...response.data.tickets.map((ticket) => `\n${formatRow(ticket)}`));
        totalPages = response.data.total_pages;
        page += 1;
      } while (page <= totalPages);
      const blob = new Blob(chunks, { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.download = `relatorio_wstech_${new Date().toISOString().split("T")[0]}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast({ title: "Erro ao exportar relatório", description: "Não foi possível exportar todos os atendimentos. Tente novamente.", variant: "destructive" });
    } finally {
      setExporting(false);
    }
  };

  const statusLabels = {
    pending: "Pendente",
    in_progress: "Em Andamento",
    resolved: "Resolvido",
    closed: "Fechado",
  };
  const priorityLabels = {
    low: "Baixa",
    medium: "Média",
    high: "Alta",
    urgent: "Urgente",
  };

  const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"];

  const statusChartData = reportData
    ? Object.entries(reportData.status_counts).map(([key, value]) => ({
        name:
          key === "pending"
            ? "Pendente"
            : key === "in_progress"
              ? "Em Andamento"
              : key === "resolved"
                ? "Resolvido"
                : key === "closed"
                  ? "Fechado"
                  : key,
        value,
      }))
    : [];

  const priorityChartData = reportData
    ? Object.entries(reportData.priority_counts).map(([key, value]) => ({
        name:
          key === "low"
            ? "Baixa"
            : key === "medium"
              ? "Média"
              : key === "high"
                ? "Alta"
                : "Urgente",
        value,
      }))
    : [];

  const categoryChartData = reportData
    ? Object.entries(reportData.category_counts).map(([key, value]) => ({
        name: key,
        value,
      }))
    : [];

  const renderPagination = () => {
    if (!reportData) return null;
    const { page, total_pages: totalPages, total_tickets: total, tickets } = reportData;
    const first = total ? (page - 1) * REPORT_PAGE_SIZE + 1 : 0;
    const last = total ? first + tickets.length - 1 : 0;
    const disabled = loading || exporting;
    return (
      <div className="flex flex-col xl:flex-row items-center justify-between gap-3 py-4">
        <div className="text-sm text-gray-600 text-center xl:text-left" aria-live="polite">
          {loading ? (
            <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Carregando...</span>
          ) : (
            <>Mostrando <span className="font-semibold text-gray-900">{first} a {last}</span> de <span className="font-semibold text-gray-900">{total}</span> atendimentos</>
          )}
          <span className="block text-xs text-gray-500 mt-1">100 por página · Página {page} de {totalPages}</span>
        </div>
        <nav aria-label="Paginação dos atendimentos" className="flex flex-wrap items-center justify-center gap-1">
          <Button variant="outline" size="icon" className="h-8 w-8" aria-label="Primeira página" title="Primeira página" disabled={disabled || page === 1} onClick={() => changePage(1)}>
            <ChevronsLeft />
          </Button>
          <Button variant="outline" size="icon" className="h-8 w-8" aria-label="Página anterior" title="Página anterior" disabled={disabled || page === 1} onClick={() => changePage(page - 1)}>
            <ChevronLeft />
          </Button>
          {getReportPages(page, totalPages).map((item) => typeof item === "number" ? (
            <Button key={item} variant={item === page ? "default" : "outline"} size="icon" className="h-8 min-w-8 w-auto px-2" aria-label={`Página ${item}`} aria-current={item === page ? "page" : undefined} disabled={disabled} onClick={() => changePage(item)}>
              {item}
            </Button>
          ) : <span key={item} className="px-1 text-gray-400" aria-hidden="true">…</span>)}
          <Button variant="outline" size="icon" className="h-8 w-8" aria-label="Próxima página" title="Próxima página" disabled={disabled || page === totalPages} onClick={() => changePage(page + 1)}>
            <ChevronRight />
          </Button>
          <Button variant="outline" size="icon" className="h-8 w-8" aria-label="Última página" title="Última página" disabled={disabled || page === totalPages} onClick={() => changePage(totalPages)}>
            <ChevronsRight />
          </Button>
        </nav>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
          <BarChart3 className="w-8 h-8 text-blue-600" />
          Relatórios
        </h1>
        <p className="text-gray-600 mt-1">
          Visualize e exporte relatórios completos dos atendimentos
        </p>
      </div>

      <div className="flex flex-col gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Filtros do Relatório</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <Label>
                Buscar (Nome, Empresa, Telefone, Documento ou Protocolo)
              </Label>
              <Input
                type="text"
                placeholder="Ex: WSTech, 11999999999, CNPJ ou TK-2026..."
                value={filters.client_search || ""}
                onChange={(e) =>
                  setFilters({ ...filters, client_search: e.target.value })
                }
                data-testid="filter-client-search"
                className="bg-blue-50/30"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-4">
              <div>
                <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_7.5rem] gap-2">
                  <div>
                    <Label htmlFor="report-start-date">Data Inicial</Label>
                    <Input id="report-start-date" type="date" value={filters.start_date}
                      onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                      data-testid="filter-start-date" />
                  </div>
                  <div>
                    <Label htmlFor="report-start-time">Hora Inicial</Label>
                    <Input id="report-start-time" type="time" value={filters.start_time}
                      onChange={(e) => setFilters({ ...filters, start_time: e.target.value })}
                      data-testid="filter-start-time" />
                  </div>
                </div>
              </div>
              <div>
                <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_7.5rem] gap-2">
                  <div>
                    <Label htmlFor="report-end-date">Data Final</Label>
                    <Input id="report-end-date" type="date" value={filters.end_date}
                      onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                      data-testid="filter-end-date" />
                  </div>
                  <div>
                    <Label htmlFor="report-end-time">Hora Final</Label>
                    <Input id="report-end-time" type="time" value={filters.end_time}
                      onChange={(e) => setFilters({ ...filters, end_time: e.target.value })}
                      data-testid="filter-end-time" />
                  </div>
                </div>
              </div>
              <div>
                <Label>Status</Label>
                <Select
                  value={filters.status}
                  onValueChange={(value) =>
                    setFilters({ ...filters, status: value })
                  }
                >
                  <SelectTrigger data-testid="filter-status">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all_">Todos</SelectItem>
                    <SelectItem value="pending">Pendente</SelectItem>
                    <SelectItem value="in_progress">Em Andamento</SelectItem>
                    <SelectItem value="resolved">Resolvido</SelectItem>
                    <SelectItem value="closed">Fechado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Atendente</Label>
                <Select
                  value={filters.assigned_to}
                  onValueChange={(value) =>
                    setFilters({ ...filters, assigned_to: value })
                  }
                >
                  <SelectTrigger data-testid="filter-attendant">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all_">Todos</SelectItem>
                    {attendants.map((att) => (
                      <SelectItem key={att.id} value={att.id}>
                        {att.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Categoria</Label>
                <Select
                  value={filters.category}
                  onValueChange={(value) =>
                    setFilters({ ...filters, category: value })
                  }
                >
                  <SelectTrigger data-testid="filter-category">
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all_">Todas</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.name}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Prioridade</Label>
                <Select
                  value={filters.priority}
                  onValueChange={(value) =>
                    setFilters({ ...filters, priority: value })
                  }
                >
                  <SelectTrigger data-testid="filter-priority">
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all_">Todas</SelectItem>
                    <SelectItem value="low">Baixa</SelectItem>
                    <SelectItem value="medium">Média</SelectItem>
                    <SelectItem value="high">Alta</SelectItem>
                    <SelectItem value="urgent">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Ordenar por</Label>
                <Select
                  value={filters.sort_by}
                  onValueChange={(value) =>
                    setFilters({ ...filters, sort_by: value })
                  }
                >
                  <SelectTrigger data-testid="report-sort-by">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="created_at">Data de criação</SelectItem>
                    <SelectItem value="duration">Tempo de atendimento</SelectItem>
                    <SelectItem value="rating">Nota</SelectItem>
                    <SelectItem value="priority">Prioridade</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Ordem</Label>
                <Select
                  value={filters.sort_direction}
                  onValueChange={(value) =>
                    setFilters({ ...filters, sort_direction: value })
                  }
                >
                  <SelectTrigger data-testid="report-sort-direction">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="desc">Maior para menor</SelectItem>
                    <SelectItem value="asc">Menor para maior</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-xs text-gray-500 mb-4">Período pela data de criação do atendimento, no seu horário local. Sem horário, considera o dia completo.</p>
            <div className="flex flex-wrap gap-4">
              <Button
                onClick={() => loadReport(filters, true)}
                disabled={loading || exporting || !filtersReady}
                data-testid="generate-report-btn"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                {loading ? "Gerando..." : "Gerar Relatório"}
              </Button>
              {reportData && (
                <Button
                  variant="outline"
                  onClick={exportReport}
                  disabled={loading || exporting}
                  data-testid="export-report-btn"
                >
                  {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  {exporting ? "Exportando..." : "Exportar CSV"}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {reportData && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-6">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total</p>
                      <p
                        className="text-3xl font-bold"
                        data-testid="report-total"
                      >
                        {reportData.total_tickets}
                      </p>
                    </div>
                    <BarChart3 className="w-10 h-10 text-blue-600" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Pendentes</p>
                      <p
                        className="text-3xl font-bold text-yellow-600"
                        data-testid="report-pending"
                      >
                        {reportData.status_counts.pending || 0}
                      </p>
                    </div>
                    <Clock className="w-10 h-10 text-yellow-600" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Em Andamento</p>
                      <p
                        className="text-3xl font-bold text-blue-600"
                        data-testid="report-progress"
                      >
                        {reportData.status_counts.in_progress || 0}
                      </p>
                    </div>
                    <TrendingUp className="w-10 h-10 text-blue-600" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Resolvidos</p>
                      <p
                        className="text-3xl font-bold text-green-600"
                        data-testid="report-resolved"
                      >
                        {reportData.status_counts.resolved || 0}
                      </p>
                    </div>
                    <CheckCircle className="w-10 h-10 text-green-600" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Fechados</p>
                      <p
                        className="text-3xl font-bold text-gray-600"
                        data-testid="report-closed"
                      >
                        {reportData.status_counts.closed || 0}
                      </p>
                    </div>
                    <XCircle className="w-10 h-10 text-gray-400" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Tempo Médio</p>
                      <p
                        className="text-2xl font-bold text-purple-600"
                        data-testid="report-avg-time"
                      >
                        {formatDuration(reportData.avg_duration_seconds)}
                      </p>
                    </div>
                    <Timer className="w-10 h-10 text-purple-600" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {reportData.attendant_stats &&
              reportData.attendant_stats.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      Desempenho por Atendente
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-3 px-4">Atendente</th>
                            <th className="text-center py-3 px-4">Total</th>
                            <th className="text-center py-3 px-4">Resolvidos</th>
                            <th className="text-center py-3 px-4">Fechados</th>
                            <th className="text-center py-3 px-4">
                              Taxa de Resolução
                            </th>
                            <th className="text-center py-3 px-4">
                              Tempo Médio
                            </th>
                            <th className="text-center py-3 px-4">
                              Nota (Média)
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.attendant_stats.map((att, idx) => (
                            <tr key={idx} className="border-b hover:bg-gray-50">
                              <td className="py-3 px-4 font-medium">
                                {att.name}
                              </td>
                              <td className="py-3 px-4 text-center">
                                {att.total}
                              </td>
                              <td className="py-3 px-4 text-center text-green-600 font-medium">
                                {att.resolved}
                              </td>
                              <td className="py-3 px-4 text-center text-gray-600 font-medium">
                                {att.closed}
                              </td>
                              <td className="py-3 px-4 text-center">
                                <span
                                  className={`px-2 py-1 rounded-full text-xs ${
                                    att.resolution_rate >= 80
                                      ? "bg-green-100 text-green-800"
                                      : att.resolution_rate >= 50
                                        ? "bg-yellow-100 text-yellow-800"
                                        : "bg-red-100 text-red-800"
                                  }`}
                                  title="Resolvidos ÷ (Resolvidos + Fechados)"
                                >
                                  {att.resolution_rate}%
                                </span>
                              </td>
                              <td className="py-3 px-4 text-center text-purple-600">
                                {formatDuration(att.avg_duration_seconds)}
                              </td>
                              <td className="py-3 px-4 text-center font-bold text-yellow-600">
                                {att.avg_rating > 0
                                  ? `⭐ ${att.avg_rating}`
                                  : "-"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Atendimentos por Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={statusChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="value" fill="#3b82f6" name="Quantidade" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Atendimentos por Prioridade</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={priorityChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(entry) => entry.name}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {priorityChartData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {categoryChartData.length > 0 && (
                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle>Atendimentos por Categoria</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={categoryChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="value" fill="#10b981" name="Quantidade" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Detalhes dos Atendimentos</CardTitle>
              </CardHeader>
              <CardContent>
                {renderPagination()}
                <div className="relative bg-white rounded-lg shadow-sm border overflow-hidden">
                  <div
                    className={`hidden lg:flex absolute left-4 top-[3.5rem] bottom-0 w-16 z-10 items-start justify-start pointer-events-none transition-opacity duration-300 ${canScrollLeft ? "opacity-100" : "opacity-0"}`}
                  >
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={scrollToLeft}
                      className="rounded-full w-8 h-8 bg-white shadow-md border-gray-200 text-blue-600 pointer-events-auto opacity-90 hover:opacity-100"
                      title="Deslizar para a esquerda"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </Button>
                  </div>

                  <div
                    className={`hidden lg:flex absolute right-4 top-[3.5rem] bottom-0 w-16 z-10 items-start justify-end pointer-events-none transition-opacity duration-300 ${canScrollRight ? "opacity-100" : "opacity-0"}`}
                  >
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={scrollToRight}
                      className="rounded-full w-8 h-8 bg-white shadow-md border-gray-200 text-blue-600 pointer-events-auto opacity-90 hover:opacity-100"
                      title="Deslizar para a direita"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </Button>
                  </div>

                  <div
                    className={`hidden lg:flex absolute left-4 bottom-[1rem] top-0 w-16 z-10 items-end justify-start pointer-events-none transition-opacity duration-300 ${canScrollLeft ? "opacity-100" : "opacity-0"}`}
                  >
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={scrollToLeft}
                      className="rounded-full w-8 h-8 bg-white shadow-md border-gray-200 text-blue-600 pointer-events-auto opacity-90 hover:opacity-100"
                      title="Deslizar para a esquerda"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </Button>
                  </div>

                  <div
                    className={`hidden lg:flex absolute right-4 bottom-[1rem] top-0 w-16 z-10 items-end justify-end pointer-events-none transition-opacity duration-300 ${canScrollRight ? "opacity-100" : "opacity-0"}`}
                  >
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={scrollToRight}
                      className="rounded-full w-8 h-8 bg-white shadow-md border-gray-200 text-blue-600 pointer-events-auto opacity-90 hover:opacity-100"
                      title="Deslizar para a direita"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </Button>
                  </div>

                  <div
                    className="overflow-x-auto custom-scrollbar pb-2"
                    ref={tableScrollRef}
                    onScroll={checkForScrollPosition}
                  >
                    <table className="w-full" data-testid="reports-table">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Protocolo
                          </th>
                          <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Cliente
                          </th>
                          <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Telefone
                          </th>
                          <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Atendente
                          </th>
                          <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Categoria
                          </th>
                          <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Nota
                          </th>
                          <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Prioridade
                          </th>
                          <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Data
                          </th>
                          <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Duração
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {reportData.tickets.map((ticket) => (
                          <tr
                            key={ticket.id}
                            className="hover:bg-gray-50 transition-colors"
                          >
                            <td className="py-3 px-4 font-mono text-sm whitespace-nowrap">
                              {ticket.protocol}
                            </td>
                            <td className="py-3 px-4 whitespace-nowrap">
                              <div
                                className="font-semibold text-gray-800 truncate max-w-[150px] sm:max-w-[200px] md:max-w-[250px]"
                                title={ticket.client_name}
                              >
                                {ticket.client_name}
                              </div>
                              {ticket.external_client_company && (
                                <div
                                  className="text-[11px] text-purple-600 font-medium truncate mt-0.5 max-w-[150px] sm:max-w-[200px] md:max-w-[250px]"
                                  title={ticket.external_client_company}
                                >
                                  🏢 {ticket.external_client_company}
                                </div>
                              )}
                            </td>
                            <td className="py-3 px-4 whitespace-nowrap">
                              {ticket.external_client_data?.real_phone ? (
                                <span className="text-green-600 font-medium">
                                  {formatWhatsAppNumber(
                                    ticket.external_client_data.real_phone,
                                  )}
                                </span>
                              ) : ticket.client_phone.length > 14 ? (
                                <span className="text-gray-500 text-xs">
                                  ID ({ticket.client_phone})
                                </span>
                              ) : (
                                formatWhatsAppNumber(ticket.client_phone)
                              )}
                            </td>
                            <td className="py-3 px-4 whitespace-nowrap">
                              <span
                                className={`${!ticket.assigned_to_name ? "text-red-600" : ""}`}
                              >
                                {ticket.closure_type === "potential" ? "Sem atendimento" : ticket.assigned_to_name || "N/A"}
                              </span>
                              {ticket.closure_type === "potential" && (
                                <span className="block text-xs text-gray-500 mt-1">
                                  Potencial encerrado por {ticket.closed_by_name || "usuário"}
                                </span>
                              )}
                            </td>
                            <td className="py-3 px-4 whitespace-nowrap">
                              {ticket.category || "-"}
                            </td>
                            <td className="py-3 px-4 whitespace-nowrap">
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-bold ${
                                  ticket.status === "pending"
                                    ? "bg-yellow-100 text-yellow-800"
                                    : ticket.status === "in_progress"
                                      ? "bg-blue-100 text-blue-800"
                                      : ticket.status === "resolved"
                                        ? "bg-green-100 text-green-800"
                                        : "bg-gray-100 text-gray-800"
                                }`}
                              >
                                {ticket.status === "pending"
                                  ? "Pendente"
                                  : ticket.status === "in_progress"
                                    ? "Em Andamento"
                                    : ticket.status === "resolved"
                                      ? "Resolvido"
                                      : "Fechado"}
                              </span>
                            </td>
                            <td className="py-3 px-4 font-medium whitespace-nowrap">
                              {ticket.rating !== null &&
                              ticket.rating !== undefined &&
                              ticket.rating >= 0
                                ? `⭐ ${ticket.rating}`
                                : "-"}
                            </td>
                            <td className="py-3 px-4 whitespace-nowrap">
                              <span
                                className={`font-medium ${
                                  ticket.priority === "low"
                                    ? "text-green-600"
                                    : ticket.priority === "medium"
                                      ? "text-yellow-600"
                                      : ticket.priority === "high"
                                        ? "text-orange-600"
                                        : "text-red-600"
                                }`}
                              >
                                {ticket.priority === "low"
                                  ? "Baixa"
                                  : ticket.priority === "medium"
                                    ? "Média"
                                    : ticket.priority === "high"
                                      ? "Alta"
                                      : "Urgente"}
                              </span>
                            </td>
                            <td className="py-3 px-4 whitespace-nowrap">
                              {new Date(ticket.created_at).toLocaleDateString(
                                "pt-BR",
                              )}
                              <span className="block text-xs text-gray-500 mt-1">
                                {new Date(ticket.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-purple-600 font-medium whitespace-nowrap">
                              {ticket.closure_type === "potential" ? "—" : formatDuration(ticket.duration_seconds)}
                            </td>
                          </tr>
                        ))}
                        {reportData.tickets.length === 0 && (
                          <tr><td colSpan={10} className="py-10 text-center text-gray-500">Nenhum atendimento encontrado para os filtros escolhidos.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                {renderPagination()}
              </CardContent>
            </Card>
          </>
        )}

        {!reportData && (
          <Card>
            <CardContent className="py-16">
              <div className="text-center text-gray-500">
                <BarChart3 className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <p className="text-lg">
                  Configure os filtros e clique em "Gerar Relatório"
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Reports;
